const FADE_DURATION = 400;
const TEXT_DELAY = 220;

class SeasonRhythmTabs extends HTMLElement {
  #controller = new AbortController();
  #isTransitioning = false;
  #activeButton = null;
  #queuedButton = null;
  #queuedFocus = false;

  connectedCallback() {
    const { signal } = this.#controller;
    this.#activeButton = this.querySelector('[data-tab-btn][aria-selected="true"]');

    this.querySelectorAll('[data-tab-btn]').forEach((button) => {
      button.addEventListener('click', () => this.#selectTab(button), { signal });
    });

    this.querySelector('[data-tab-prev]')?.addEventListener('click', () => this.#selectRelativeTab(-1), { signal });
    this.querySelector('[data-tab-next]')?.addEventListener('click', () => this.#selectRelativeTab(1), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #selectRelativeTab(direction) {
    const buttons = [...this.querySelectorAll('[data-tab-btn]')];
    const activeIndex = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    const nextIndex = (activeIndex + direction + buttons.length) % buttons.length;
    const nextButton = buttons[nextIndex];
    if (!nextButton) return;

    this.#selectTab(nextButton, true);
  }

  async #selectTab(nextButton, shouldFocus = false) {
    if (!this.#isTransitioning && nextButton === this.#activeButton) return;

    if (this.#isTransitioning) {
      this.#queuedButton = nextButton;
      this.#queuedFocus ||= shouldFocus;
      this.#setSelected(nextButton);
      return;
    }

    const activeButton = this.#activeButton;
    if (!(activeButton instanceof HTMLElement)) return;

    const nextPanelId = nextButton.getAttribute('aria-controls');
    const nextPanel = nextPanelId ? this.querySelector(`#${nextPanelId}`) : null;
    const activePanelId = activeButton?.getAttribute('aria-controls');
    const activePanel = activePanelId ? this.querySelector(`#${activePanelId}`) : null;
    if (!(nextPanel instanceof HTMLElement) || !(activePanel instanceof HTMLElement)) return;

    this.#isTransitioning = true;
    this.#setSelected(nextButton);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      activePanel.hidden = true;
      nextPanel.hidden = false;
      this.#activeButton = nextButton;
      this.#isTransitioning = false;

      if (shouldFocus) nextButton.focus({ preventScroll: true });
      return;
    }

    activePanel.classList.add('season-rhythm__panel--leaving');
    await this.#wait(FADE_DURATION);

    activePanel.hidden = true;
    activePanel.classList.remove('season-rhythm__panel--leaving');
    nextPanel.hidden = false;
    nextPanel.classList.add('season-rhythm__panel--entering');

    requestAnimationFrame(() => nextPanel.classList.add('season-rhythm__panel--image-visible'));
    await this.#wait(TEXT_DELAY);
    nextPanel.classList.add('season-rhythm__panel--text-visible');
    await this.#wait(FADE_DURATION);

    nextPanel.classList.remove(
      'season-rhythm__panel--entering',
      'season-rhythm__panel--image-visible',
      'season-rhythm__panel--text-visible'
    );
    this.#activeButton = nextButton;
    this.#isTransitioning = false;

    if (shouldFocus) nextButton.focus({ preventScroll: true });

    const queuedButton = this.#queuedButton;
    const queuedFocus = this.#queuedFocus;
    this.#queuedButton = null;
    this.#queuedFocus = false;

    if (queuedButton && queuedButton !== this.#activeButton) {
      void this.#selectTab(queuedButton, queuedFocus);
    }
  }

  #setSelected(activeButton) {
    const buttons = [...this.querySelectorAll('[data-tab-btn]')];
    const index = buttons.indexOf(activeButton);

    buttons.forEach((button) => {
      button.setAttribute('aria-selected', button === activeButton ? 'true' : 'false');
    });

    this.dispatchEvent(
      new CustomEvent('season-rhythm-tabs:change', {
        bubbles: true,
        detail: { index, button: activeButton },
      })
    );
  }

  #wait(duration) {
    return new Promise((resolve) => window.setTimeout(resolve, duration));
  }
}

if (!customElements.get('season-rhythm-tabs')) {
  customElements.define('season-rhythm-tabs', SeasonRhythmTabs);
}
